/* =============================================================================
 *  Kreux Matrix — Page 5 · Terminal & Footer Layer · V3
 *  "Enterprise Compliance · Hex-Matrix · FOMO Protocol"
 *
 *  Self-contained · Brand vars only · No React state · No hover-only triggers.
 *  Anchor CTA targets #hero-cta for round-trip back to Page 1.
 * ===========================================================================*/

import { Mail, Phone } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ff176637-490c-4d8d-8821-0039665e7b20/artifacts/1ppcjvci_Web%20logo.png";

const dict = {
  en: {
    Overline: "FINAL PROTOCOL",
    Headline: "Zero Exposure. Infinite Leverage.",
    Body: "Built for stringent European data sovereignty. Engineered for global dominance. Every day your operations rely on human latency is market share ceded to those who don't. The Kreux Matrix deploys on isolated, sovereign servers—ensuring your proprietary data never trains public models. The gap is widening. Choose your side.",
    CtaButton: "Initiate Architecture Call",
    CtaTrust: "100% GDPR Compliant. Zero Data Retention in Public LLMs.",
    ContactLabel: "Contact",
    FooterStatus: "SYSTEM STATUS: SECURE. SERVERS: FRANKFURT, DE.",
    FooterLinks: [
      { label: "Imprint",          href: "/imprint" },
      { label: "Privacy Policy",   href: "/privacy" },
      { label: "Terms of Service", href: "/terms"   }
    ],
    Copyright: "© 2026 Kreux Matrix. All rights reserved."
  },
  de: {
    Overline: "ABSCHLUSSPROTOKOLL",
    Headline: "Null Risiko. Unendlicher Hebel.",
    Body: "Entwickelt für strenge europäische Datensouveränität. Konstruiert für globale Dominanz. Jeder Tag, an dem Ihre Prozesse auf menschliche Latenz angewiesen sind, ist verlorener Marktanteil an die Konkurrenz. Die Kreux-Matrix läuft auf isolierten, souveränen Servern – Ihre proprietären Daten trainieren niemals öffentliche Modelle. Die Lücke vergrößert sich. Wählen Sie Ihre Seite.",
    CtaButton: "Architektur-Gespräch initiieren",
    CtaTrust: "100% DSGVO-konform. Keine Datenspeicherung in öffentlichen LLMs.",
    ContactLabel: "Kontakt",
    FooterStatus: "SYSTEMSTATUS: SICHER. SERVER: FRANKFURT, DE.",
    FooterLinks: [
      { label: "Impressum",           href: "/imprint" },
      { label: "Datenschutzerklärung", href: "/privacy" },
      { label: "AGB",                  href: "/terms"   }
    ],
    Copyright: "© 2026 Kreux Matrix. Alle Rechte vorbehalten."
  }
};

/* ─── Background Hexagon Matrix (mirrors Page 4) ──────────────────────── */
const HexMatrix = () => (
  <svg
    aria-hidden="true"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
    className="absolute inset-0"
  >
    <defs>
      <pattern id="kx-hex-tl" x="0" y="0" width="56" height="98" patternUnits="userSpaceOnUse">
        <path
          d="M28 0 L56 16 L56 48 L28 64 L0 48 L0 16 Z M0 48 L0 80 L28 96 L56 80 L56 48"
          fill="none"
          stroke="rgba(255,255,255,0.04)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#kx-hex-tl)" />
  </svg>
);

/* ─── Arrow glyph (up-right) ──────────────────────────────────────────── */
const ArrowUpRight = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

/* ─── Social glyphs ───────────────────────────────────────────────────── */
const InstagramGlyph = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" strokeWidth="2.5" />
  </svg>
);
const LinkedInGlyph = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const TerminalLayer = ({ language = "en" }) => {
  const t = dict[language] || dict["en"];

  return (
    <section
      id="terminal-layer"
      data-testid="terminal-layer"
      className="relative w-full min-h-screen flex flex-col justify-center items-center bg-transparent overflow-hidden font-sans"
      style={{
        "--il-white": "#FFFFFF",
        "--il-cyan":  "#00E5FF",
        "--il-cyan-rgb": "0, 229, 255",
        "--il-steel": "#1B3245",
        "--il-navy":  "#112332",
        "--il-amber": "#FFB300",
      }}
    >
      <style>{styles}</style>

      {/* Background Hexagon Matrix — z-0 */}
      <div className="absolute inset-0 z-0 opacity-20 animate-pulse pointer-events-none" style={{ animationDuration: "6s" }}>
        <HexMatrix />
      </div>

      {/* Content Wrapper — z-20 */}
      <div className="relative z-20 max-w-4xl mx-auto text-center px-6 py-24 flex flex-col items-center">
        <span
          className="text-xs font-mono tracking-[0.3em] text-[var(--il-amber)] mb-6 uppercase animate-pulse block"
          data-testid="tl-overline"
        >
          {t.Overline}
        </span>

        <h2
          className="text-5xl md:text-7xl font-light tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-white via-white/80 to-white/20 drop-shadow-[0_0_30px_rgba(255,255,255,0.1)] leading-tight pb-2 mb-8 m-0"
          data-testid="tl-headline"
        >
          {t.Headline}
        </h2>

        <p
          className="text-lg md:text-xl text-white/40 leading-relaxed font-light max-w-2xl mx-auto mb-14 tracking-wide m-0"
          data-testid="tl-body"
        >
          {t.Body}
        </p>

        {/* CTA · Floating Diamond Boomerang (mirrors Hero CTA aesthetic) */}
        <div className="tl-float inline-block">
          <a
            href="#hero-cta"
            data-testid="tl-cta"
            className="relative group overflow-hidden flex items-center justify-center gap-4 px-12 py-6 bg-[#050505]/80 backdrop-blur-xl border border-white/10 hover:border-[var(--il-cyan)]/50 transition-all duration-700 shadow-[0_0_40px_-10px_rgba(var(--il-cyan-rgb),0.4)] no-underline"
          >
            {/* Continuous shine sweep */}
            <span
              aria-hidden="true"
              className="tl-shine pointer-events-none absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent"
            />
            {/* Diamond accent */}
            <span
              aria-hidden="true"
              className="relative w-2.5 h-2.5 rotate-45 bg-[var(--il-cyan)] shadow-[0_0_15px_var(--il-cyan)] group-hover:scale-110 transition-transform duration-500"
            />
            {/* Label */}
            <span className="relative text-xl font-medium tracking-widest uppercase text-white group-hover:text-[var(--il-cyan)] transition-colors duration-500">
              {t.CtaButton}
            </span>
            {/* Cyan arrow */}
            <span
              aria-hidden="true"
              className="relative inline-flex items-center justify-center text-[var(--il-cyan)] group-hover:translate-x-2 transition-transform duration-500"
            >
              <ArrowUpRight width="20" height="20" />
            </span>
          </a>
        </div>

        {/* Trust label */}
        <div className="mt-8 flex items-center justify-center text-[10px] font-mono text-white/30 uppercase tracking-[0.1em]" data-testid="tl-trust">
          <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse mr-2 shadow-[0_0_8px_rgba(34,197,94,0.7)]" />
          <span>{t.CtaTrust}</span>
        </div>
      </div>

      {/* ─── Compliance & Contact Footer — z-30 ─────────────────────── */}
      <footer
        data-testid="tl-footer"
        className="relative z-30 w-full border-t border-white/10 bg-[#020202]/90 backdrop-blur-3xl pt-16 pb-8 px-6 lg:px-12 mt-auto"
      >
        <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-12 max-w-[90rem] mx-auto">
          {/* Left — Branding & Status */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={LOGO_URL}
                alt="Kreux Matrix"
                className="w-8 h-8 object-contain select-none"
                draggable="false"
                data-testid="tl-footer-logo"
              />
              <span
                className="text-xl md:text-2xl font-light tracking-tighter text-white uppercase"
                data-testid="tl-footer-wordmark"
              >
                Kreux Matrix
              </span>
            </div>
            <span
              className="text-[10px] font-mono text-[var(--il-cyan)] tracking-widest"
              style={{ textShadow: "0 0 8px rgba(0,229,255,0.6)" }}
              data-testid="tl-footer-status"
            >
              {t.FooterStatus}
            </span>
          </div>

          {/* Middle — Contact & Socials */}
          <div className="flex flex-col gap-4 items-center md:items-start">
            <div
              className="text-sm text-gray-400 font-medium tracking-wide flex flex-wrap items-center justify-center md:justify-start gap-x-3 gap-y-1"
              data-testid="tl-contact"
            >
              <span className="text-white/40">{t.ContactLabel}:</span>
              <a
                href="mailto:intelligence@kreuxmatrix.de"
                data-testid="tl-contact-email"
                className="inline-flex items-center gap-2 text-sm text-gray-400 font-medium tracking-wide hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_8px_var(--il-cyan)] transition-all duration-300 no-underline"
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                <span>intelligence@kreuxmatrix.de</span>
              </a>
              <span aria-hidden="true" className="text-white/20">·</span>
              <a
                href="tel:+4917685984847"
                data-testid="tl-contact-phone"
                className="inline-flex items-center gap-2 text-sm text-gray-400 font-medium tracking-wide hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_8px_var(--il-cyan)] transition-all duration-300 no-underline"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                <span>+49 176 85984847</span>
              </a>
            </div>
            <div className="flex flex-row gap-4">
              <a
                href="https://www.instagram.com/kreuxmatrixaistudio/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="tl-social-instagram"
                className="text-gray-400 hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_8px_var(--il-cyan)] transition-all duration-300 inline-flex"
              >
                <InstagramGlyph width="20" height="20" />
              </a>
              <a
                href="https://www.linkedin.com/company/136074047/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="tl-social-linkedin"
                className="text-gray-400 hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_8px_var(--il-cyan)] transition-all duration-300 inline-flex"
              >
                <LinkedInGlyph width="20" height="20" />
              </a>
            </div>
          </div>

          {/* Right — Legal Links */}
          <nav
            data-testid="tl-legal-links"
            className="flex flex-row flex-wrap justify-center md:justify-end gap-6"
          >
            {t.FooterLinks.map((link, i) => (
              <a
                key={i}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-white/40 hover:text-[var(--il-cyan)] hover:drop-shadow-[0_0_8px_var(--il-cyan)] transition-all duration-300 uppercase tracking-wider no-underline"
                data-testid={`tl-legal-link-${i}`}
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>

        {/* Bottom bar */}
        <div className="w-full max-w-[90rem] mx-auto mt-16 pt-6 border-t border-white/5 flex justify-center text-center md:text-left">
          <span
            className="text-[10px] text-white/30"
            data-testid="tl-copyright"
          >
            {t.Copyright}
          </span>
        </div>
      </footer>
    </section>
  );
};

const styles = `
/* CTA float — slow vertical breathe */
@keyframes tlFloat {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
}
.tl-float { animation: tlFloat 3s ease-in-out infinite; will-change: transform; }

/* Continuous shine sweep across the pill */
@keyframes tlShine {
  0%   { transform: translateX(-110%); }
  60%  { transform: translateX(110%);  }
  100% { transform: translateX(110%);  }
}
.tl-shine { animation: tlShine 3.6s ease-in-out infinite; will-change: transform; }

@media (prefers-reduced-motion: reduce) {
  .tl-float, .tl-shine { animation: none !important; }
  #terminal-layer * { animation: none !important; transition: none !important; }
}
`;

export default TerminalLayer;
